import { PERMISSIONS, type Permission } from "../lib/permissions.ts";
import type { WorkSection } from "../lib/telemetry/events.ts";

/**
 * Los dos modos de trabajo (§9.2).
 *
 * No son pestañas de contenido: son dos formas de estar en la aplicación, y
 * cada una tiene su propio armazón.
 *
 * - `chat` es el único layout conversacional: historial y memoria a la
 *   izquierda --y dentro de la memoria, las mecánicas (§7.3)--, la
 *   conversación al centro y el redactor abajo.
 * - `work` es un panel de control de los de siempre: sus secciones a la
 *   izquierda, contenido al centro. Ni historial ni redactor.
 *
 * Antes eran tres pestañas --Chat, Work y Mecánicas-- que compartían el
 * armazón del chat, así que las dos últimas heredaban el historial y el
 * selector de memoria, que allí no significan nada. Y las pestañas se
 * escondían al abrir una conversación: la única salida era pulsar
 * "Conversación nueva", que es otra acción y además destruye el turno.
 *
 * El conmutador vive arriba, en el mismo sitio en los dos armazones, y no
 * desaparece nunca. Lo que escriban las dos pantallas pasa por las MISMAS
 * rutas del catálogo que usa el agente (§6), así que no hay una segunda puerta
 * a la escritura.
 *
 * El modo ES la ruta: la tabla de abajo es lo único que traduce entre las dos
 * formas de nombrarlo.
 */
export const WORKSPACE_MODES = ["chat", "work"] as const;

export type WorkspaceMode = (typeof WORKSPACE_MODES)[number];

/**
 * Chat y Work, y no hay una tercera palabra.
 *
 * El modo se llama igual en el conmutador, en la URL y en el código: en cuanto
 * la pantalla dijera "Panel" y la ruta `/work`, habría dos nombres para lo
 * mismo y la conversación con el usuario tendría que traducir.
 */
export const MODE_LABELS: Record<WorkspaceMode, string> = {
  chat: "Chat",
  work: "Work",
};

const MODE_PATHS: Record<WorkspaceMode, "/" | "/work"> = {
  chat: "/",
  work: "/work",
};

export const pathOf = (
  mode: WorkspaceMode,
): (typeof MODE_PATHS)[WorkspaceMode] => MODE_PATHS[mode];

export const modeOf = (pathname: string): WorkspaceMode =>
  pathname.startsWith("/work") ? "work" : "chat";

/**
 * Las secciones del panel, en el orden en que se leen.
 *
 * Solo las que existen de verdad. Puntos (§7.4) y alertas (§7.5) no están
 * construidos en el backend, así que no aparecen: una entrada de menú que
 * lleva a una pantalla vacía promete algo que no existe. Las alertas que YA se
 * pueden observar viven dentro del Resumen, que es el panorama.
 *
 * Cargos y regiones van pegados a Participantes y no al final: son lo que se
 * elige al vincular a alguien, así que quien viene de mirar el padrón los
 * busca ahí al lado. Antes el cargo era un enum en el código y la región un
 * texto libre; ahora son dos catálogos que alguien administra.
 */
export const DASHBOARD_SECTIONS = [
  {
    key: "overview",
    label: "Resumen",
    path: "/work",
    permission: PERMISSIONS.batchesRead,
  },
  {
    key: "participants",
    label: "Participantes",
    path: "/work/participantes",
    permission: PERMISSIONS.participantsRead,
  },
  {
    key: "positions",
    label: "Cargos",
    path: "/work/cargos",
    permission: PERMISSIONS.positionsRead,
  },
  {
    key: "regions",
    label: "Regiones",
    path: "/work/regiones",
    permission: PERMISSIONS.regionsRead,
  },
  {
    key: "kpis",
    label: "KPIs",
    path: "/work/kpis",
    permission: PERMISSIONS.kpisRead,
  },
  {
    key: "usage",
    label: "Consumo",
    path: "/work/consumo",
    permission: PERMISSIONS.usageRead,
  },
] as const;

export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

/**
 * Cómo se llama lo que se está mirando, para la izquierda de la cabecera.
 *
 * El detalle de un lote no es una sección --no tiene entrada de menú-- pero sí
 * tiene nombre, porque la cabecera nunca se queda muda.
 */
export const dashboardTitleOf = (pathname: string): string => {
  if (pathname.startsWith("/work/lotes")) return "Lote";
  return dashboardSectionOf(pathname)?.label ?? "Work";
};

/** La sección en la que se está, si es una del menú. */
export const dashboardSectionOf = (
  pathname: string,
): DashboardSection | undefined =>
  [...DASHBOARD_SECTIONS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((candidate) => pathname.startsWith(candidate.path));

/**
 * La sección de Work como dimensión de la telemetría: la `key` del menú, o
 * `batch` en el detalle de un lote.
 */
export const workSectionOf = (pathname: string): WorkSection =>
  pathname.startsWith("/work/lotes")
    ? "batch"
    : (dashboardSectionOf(pathname)?.key ?? "overview");

/**
 * Qué permiso hace falta para ver lo que hay en esa ruta del panel. El
 * detalle de un lote no es una sección, pero se lee con lo mismo que el
 * Resumen: los lotes.
 */
export const dashboardPermissionOf = (pathname: string): Permission =>
  pathname.startsWith("/work/lotes")
    ? PERMISSIONS.batchesRead
    : (dashboardSectionOf(pathname)?.permission ?? PERMISSIONS.access);
